import { SPREADSHEET_TABLE_CONSTANTS, SpreadsheetTableClipboardData, SpreadsheetTableColumn, SpreadsheetTableDefaultDataItem, SpreadsheetTableSelect } from "../table.types";
import { formatDateForInput } from "../data-converter";
import { isSystemDateField } from "../filter-utils";
import { parseDateString } from "@modules/datasheets/utils/date-validation";

/**
 * Copies data to clipboard using the modern Clipboard API with fallback
 */
export const copyToClipboard = async (data: SpreadsheetTableClipboardData): Promise<boolean> => {
  try {
    // Try modern Clipboard API first
    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItems = [
        new ClipboardItem({
          'text/plain': new Blob([data.text], { type: 'text/plain' })
          // Remove HTML completely to avoid formatting issues in text editors
        })
      ];
      await navigator.clipboard.write(clipboardItems);
      return true;
    }

    // Fallback for older browsers - use plain text only
    const textArea = document.createElement('textarea');
    textArea.value = data.text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    return successful;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};

/**
 * Reads data from clipboard
 */
export const readFromClipboard = async (): Promise<string> => {
  try {
    // Try modern Clipboard API first
    if (navigator.clipboard && window.ClipboardItem) {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        if (item.types.includes('text/plain')) {
          const blob = await item.getType('text/plain');
          return await blob.text();
        }
      }
    }

    // Fallback for older browsers
    // Note: This requires user interaction and may not work in all browsers
    return '';
  } catch (error) {
    console.error('Failed to read from clipboard:', error);
    return '';
  }
};

/**
 * Formats selected spreadsheet data for clipboard copying
 */
export const formatSpreadsheetDataForClipboard = (
  data: Array<SpreadsheetTableDefaultDataItem>,
  columns: Array<SpreadsheetTableColumn>,
  selection: SpreadsheetTableSelect
): SpreadsheetTableClipboardData => {
  const { start, end } = selection;
  
  // Determine the range of rows and columns to copy
  const startRow = Math.min(start.rowIndex, end.rowIndex);
  const endRow = Math.max(start.rowIndex, end.rowIndex);
  
  // Selection columnId now represents UI column index directly
  const startColIndex = Math.min(start.columnId as unknown as number, end.columnId as unknown as number);
  const endColIndex = Math.max(start.columnId as unknown as number, end.columnId as unknown as number);
  
  // Extract the selected columns
  const selectedColumns = columns.slice(startColIndex, endColIndex + 1);
  
  // Build the text content with tabs between columns and newlines between rows
  const textRows: string[] = [];
  
  // Add data rows only (no headers)
  for (let rowIndex = startRow; rowIndex <= endRow; rowIndex++) {
    const rowData = data[rowIndex];
    if (!rowData) continue;
    
    const rowValues = selectedColumns.map(col => {
      const rawValue = col.accessorKey ? rowData[col.accessorKey] : '';
      let value = rawValue;

      if (
        col.accessorKey &&
        col.typeId === SPREADSHEET_TABLE_CONSTANTS.FIELD_TYPES.IMAGE
      ) {
        value = rowData[col.accessorKey].name;
      }

      if (
        col.accessorKey &&
        rawValue != null &&
        (col.typeId === 2 || (col.system && isSystemDateField(col.id)))
      ) {
        value = formatDateForInput(rawValue);
      }

      // Convert value to string and handle null/undefined
      return value != null ? String(value) : '';
    });
    
    textRows.push(rowValues.join('\t'));
  }
  
  const text = textRows.join('\n');
  
  // Return only plain text to avoid formatting issues in text editors
  return { text, html: '' };
};

/**
 * Parses clipboard data for pasting into spreadsheet
 * This function is called when pasting non-tabular data (no tabs/newlines)
 */
export const parseClipboardDataForPaste = (clipboardText: string): string[] => {
  // Handle tab-separated values (from Excel or other spreadsheets)
  if (clipboardText.includes('\t')) {
    return clipboardText.split('\t').map(value => value.trim());
  }
  
  // Handle comma-separated values (CSV)
  // BUT: Check if the text matches a valid date format first
  // If it's a date (e.g., "Sep 13, 2011"), don't split it
  if (clipboardText.includes(',')) {
    const trimmedText = clipboardText.trim();
    // Check if the entire text matches a valid date format
    const parsedDate = parseDateString(trimmedText);
    if (parsedDate !== null) {
      // It's a valid date format - don't split, return as single value
      return [trimmedText];
    }
    // Not a date - split on commas for CSV
    return clipboardText.split(',').map(value => value.trim());
  }
  
  // Single value
  return [clipboardText.trim()];
};

/**
 * Checks if a keyboard event is CTRL+C
 */
export const isCopyKeyCombination = (event: KeyboardEvent): boolean => {
  return (event.ctrlKey || event.metaKey) && event.key === 'c';
};

/**
 * Checks if a keyboard event is CTRL+X
 */
export const isCutKeyCombination = (event: KeyboardEvent): boolean => {
  return (event.ctrlKey || event.metaKey) && event.key === 'x';
};

/**
 * Checks if a keyboard event is CTRL+V
 */
export const isPasteKeyCombination = (event: KeyboardEvent): boolean => {
  return (event.ctrlKey || event.metaKey) && event.key === 'v';
};

/**
 * Checks if a keyboard event is CTRL+A (select all)
 */
export const isSelectAllKeyCombination = (event: KeyboardEvent): boolean => {
  return (event.ctrlKey || event.metaKey) && event.key === 'a';
};

/**
 * Checks if a keyboard event is CTRL+Z (undo)
 */
export const isUndoKeyCombination = (event: KeyboardEvent): boolean => {
  return (event.ctrlKey || event.metaKey) && event.key === 'z';
};

/**
 * Checks if a keyboard event is CTRL+Y (redo)
 */
export const isRedoKeyCombination = (event: KeyboardEvent): boolean => {
  return (event.ctrlKey || event.metaKey) && event.key === 'y';
};

/**
 * Parses clipboard data as a 2D matrix for Excel-like pasting
 * Returns an array of arrays where each inner array represents a row
 */
export const parseClipboardDataAsMatrix = (clipboardText: string): string[][] => {
  // Split by newlines to get rows
  const rows = clipboardText.split('\n').filter(row => row.trim() !== '');
  
  // Split each row by tabs to get columns
  return rows.map(row => 
    row.split('\t').map(cell => cell.trim())
  );
};

/**
 * Determines if clipboard data contains tabular data (multiple columns or rows)
 */
export const isTabularClipboardData = (clipboardText: string): boolean => {
  const hasTabs = clipboardText.includes('\t');
  const hasNewlines = clipboardText.includes('\n');
  const hasMultipleRows = clipboardText.split('\n').length > 1;
  const hasMultipleColumns = clipboardText.split('\t').length > 1;
  
  return hasTabs || (hasNewlines && hasMultipleRows) || hasMultipleColumns;
};
